import React from 'react';
import {
  Button,
  ButtonContent,
  IconSlot,
  LoadingIndicator,
  SuccessMark,
} from './nav-button.styled';
import { Pop } from '../../motion';
import { SuccessTick, useSuccessHold } from '../button/app-button';
import { Text, font } from '../../style-utils/text';
import { gradient, palette } from '../../style-utils/theme';
import { RightArrow } from '../icons/right-arrow';
import { LeftArrow } from '../icons/left-arrow';
import { PlusButton } from '../icons/plus-icon';

interface NavButtonProps {
  text: string;
  /**
   * OPTIONAL, AND DEFAULTED TO THE ACCESSIBLE PRIMARY PAIR.
   *
   * These were required, so seven call sites wrote
   * `background={palette.iris} textColor={palette.white}` by hand — white on
   * #00A19A is 3.20:1, under the 4.5:1 floor for a 14px label, and it is the
   * pairing the theme fix removed from AppButton. A component whose colours are
   * a caller's decision will be given the wrong ones eventually; the wizard's
   * Continue button has exactly one correct look.
   */
  background?: string;
  textColor?: string;
  direction: 'back' | 'forward' | 'increment' | undefined;
  onClick?: () => void;
  disabled: boolean;
  loading?: boolean;
  /** Swap the label for a popping tick at the same width — see AppButton. */
  success?: boolean;
}

export const NavButton: React.FC<NavButtonProps> = ({
  text,
  // gradient.primary / white — the same treatment AppButton's primary variant
  // uses, measured at 4.62:1 and 5.07:1 against its two stops.
  background = gradient.primary,
  textColor = palette.white,
  direction,
  onClick,
  disabled,
  loading = false,
  success = false,
}) => {
  const showTick = useSuccessHold(success) && !loading;

  return (
    <Button
      type="button"
      disabled={disabled || loading}
      onClick={onClick}
      aria-busy={loading || undefined}
      aria-label={showTick ? 'Done' : undefined}
      data-success={showTick || undefined}
      background={background}
      width={direction === 'back' ? '72' : undefined}
    >
      <ButtonContent $hidden={loading || showTick}>
        {direction === 'back' ? (
          <IconSlot className="nav-arrow-back">
            <LeftArrow />
          </IconSlot>
        ) : null}
        <Text
          as="span"
          fontSize={font.size.xs}
          fontFamily={font.family}
          mt={0}
          mb={0}
          align={'center'}
          color={disabled ? palette.slateLight : textColor}
          fontWeight={600}
        >
          {text}
        </Text>
        {direction === 'increment' ? (
          <IconSlot>
            <PlusButton />
          </IconSlot>
        ) : null}
        {direction === 'forward' ? (
          <IconSlot className="nav-arrow-forward">
            <RightArrow />
          </IconSlot>
        ) : null}
      </ButtonContent>
      {loading ? <LoadingIndicator aria-hidden="true" /> : null}
      {showTick ? (
        <SuccessMark aria-hidden="true">
          <Pop>
            <SuccessTick />
          </Pop>
        </SuccessMark>
      ) : null}
    </Button>
  );
};
